import { useState, useRef, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, Stack } from "expo-router";
import { useForm } from "react-hook-form";
import { ScrollView, Text, View } from "react-native";
import { BackButton } from "@/shared/components/ui/back-button";
import { ScreenContainer } from "@/shared/components/ui/screen-container";

import { useLogin } from "@/features/auth/api/auth";
import { apiFetch } from "@/shared/lib/bff/client";
import { useResendCooldown } from "@/features/auth/hooks/use-resend-cooldown";
import { FormInput } from "@/shared/components/ui/form-input";
import { Button } from "@/shared/components/ui/button";
import {
  ResetEmailFormData,
  resetEmailSchema,
  ResetCodeFormData,
  resetCodeSchema,
  ResetPasswordFormData,
  resetPasswordSchema,
} from "@/features/auth/schemas/auth";

type WizardData = {
  email: string;
  code: string;
};

function EmailStep({
  defaultEmail,
  error,
  isLoading,
  onNext,
}: {
  defaultEmail: string;
  error?: string | null;
  isLoading: boolean;
  onNext: (email: string) => void;
}) {
  const { control, handleSubmit } = useForm<ResetEmailFormData>({
    resolver: zodResolver(resetEmailSchema),
    defaultValues: { email: defaultEmail },
  });

  return (
    <>
      <Text className="text-foreground font-display mb-2 text-2xl font-semibold">
        Esqueceu sua senha?
      </Text>

      <Text className="text-muted-foreground mb-6 text-sm">
        Se este e-mail estiver cadastrado, você receberá um código de
        verificação.
      </Text>

      <FormInput
        control={control}
        name="email"
        label="E-mail"
        placeholder="seuemail@exemplo.com"
        keyboardType="email-address"
        autoComplete="email"
      />

      {error && <Text className="text-destructive mb-2 text-sm">{error}</Text>}

      <Button
        size="lg"
        className="mt-4"
        onPress={handleSubmit((data) => onNext(data.email))}
        loading={isLoading}
      >
        Enviar código
      </Button>
    </>
  );
}

function CodeStep({
  email,
  error,
  isLoading,
  isSendingCode,
  isOnCooldown,
  secondsLeft,
  onNext,
  onResend,
}: {
  email: string;
  error?: string | null;
  isLoading: boolean;
  isSendingCode: boolean;
  isOnCooldown: boolean;
  secondsLeft: number;
  onNext: (code: string) => void;
  onResend: () => void;
}) {
  const { control, handleSubmit } = useForm<ResetCodeFormData>({
    resolver: zodResolver(resetCodeSchema),
    defaultValues: { code: "" },
  });

  return (
    <>
      <Text className="text-foreground font-display mb-2 text-2xl font-semibold">
        Digite o código que enviamos para o seu e-mail
      </Text>

      <Text className="text-muted-foreground mb-6 text-sm">{email}</Text>

      <FormInput
        control={control}
        name="code"
        label="Código"
        placeholder="000000"
        keyboardType="number-pad"
        autoComplete="one-time-code"
      />

      {error && <Text className="text-destructive mb-2 text-sm">{error}</Text>}

      <Button
        size="lg"
        className="mt-4"
        onPress={handleSubmit((data) => onNext(data.code))}
        loading={isLoading}
      >
        Confirmar
      </Button>

      <Button
        variant="ghost"
        size="lg"
        className="mt-2"
        onPress={onResend}
        loading={isSendingCode}
        disabled={isOnCooldown}
      >
        {isOnCooldown ? `Reenviar código (${secondsLeft}s)` : "Reenviar código"}
      </Button>
    </>
  );
}

function PasswordStep({
  onSubmit,
  error,
  isLoading,
}: {
  onSubmit: (password: string) => void;
  error?: string | null;
  isLoading: boolean;
}) {
  const { control, handleSubmit } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  return (
    <>
      <Text className="text-foreground font-display mb-6 text-2xl font-semibold">
        Crie uma nova senha
      </Text>

      <FormInput
        control={control}
        name="password"
        label="Nova senha"
        placeholder="Sua nova senha"
        secureTextEntry
        showPasswordToggle
        autoComplete="password-new"
      />

      <FormInput
        control={control}
        name="confirmPassword"
        label="Confirmar senha"
        placeholder="Confirme sua nova senha"
        secureTextEntry
        showPasswordToggle
        autoComplete="password-new"
      />

      {error && <Text className="text-destructive mb-2 text-sm">{error}</Text>}

      <Button
        className="mt-4"
        size="lg"
        onPress={handleSubmit((data) => onSubmit(data.password))}
        loading={isLoading}
      >
        Redefinir senha
      </Button>
    </>
  );
}

export default function ForgotPasswordScreen() {
  const router = useRouter();
  const login = useLogin();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const wizardData = useRef<Partial<WizardData>>({});
  const [emailError, setEmailError] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const { secondsLeft, isOnCooldown, startCooldown } = useResendCooldown();

  const handleBack = useCallback(() => {
    if (currentStep === 1) {
      router.back();
    } else {
      setCurrentStep((s) => (s - 1) as 1 | 2 | 3);
    }
  }, [currentStep, router]);

  const sendResetCode = useCallback(
    async (email: string) => {
      setIsSendingCode(true);
      try {
        await apiFetch("/api/auth/send-reset-code", {
          method: "POST",
          body: JSON.stringify({ email }),
        });
        startCooldown();
        return true;
      } catch (err) {
        setEmailError(
          err instanceof Error ? err.message : "Erro ao enviar código"
        );
        return false;
      } finally {
        setIsSendingCode(false);
      }
    },
    [startCooldown]
  );

  const handleEmailNext = useCallback(
    async (email: string) => {
      wizardData.current.email = email;
      setEmailError(null);
      const ok = await sendResetCode(email);
      if (ok) setCurrentStep(2);
    },
    [sendResetCode]
  );

  const handleCodeNext = useCallback((code: string) => {
    wizardData.current.code = code;
    setResetError(null);
    setCurrentStep(3);
  }, []);

  const handlePasswordSubmit = useCallback(
    async (password: string) => {
      setIsResetting(true);
      setResetError(null);
      try {
        await apiFetch("/api/auth/reset-password", {
          method: "POST",
          body: JSON.stringify({
            email: wizardData.current.email,
            code: wizardData.current.code,
            password,
          }),
        });

        // Auto-login with the new password
        login.mutate(
          { email: wizardData.current.email!, password },
          {
            onSuccess: () => {
              router.replace("/");
            },
            onError: () => {
              // Password was reset but auto-login failed — go to login
              router.replace("/login");
            },
          }
        );
      } catch (err) {
        setResetError(
          err instanceof Error ? err.message : "Erro ao redefinir senha"
        );
      } finally {
        setIsResetting(false);
      }
    },
    [login, router]
  );

  return (
    <>
      <Stack.Screen
        options={{ title: "Forgot Password", headerShown: false }}
      />
      <ScreenContainer style={{ paddingTop: 0 }}>
        <ScrollView
          className="flex-1"
          contentContainerClassName="grow"
          keyboardShouldPersistTaps="handled"
        >
          <View className="p-6">
            <BackButton
              className="mt-8 mb-8"
              onPress={handleBack}
              accessibilityLabel="Voltar"
            />

            <Text className="text-muted-foreground mb-2 text-sm">
              Recuperação de conta
            </Text>

            {currentStep === 1 && (
              <EmailStep
                defaultEmail={wizardData.current.email ?? ""}
                error={emailError}
                isLoading={isSendingCode}
                onNext={handleEmailNext}
              />
            )}
            {currentStep === 2 && (
              <CodeStep
                email={wizardData.current.email!}
                error={null}
                isLoading={false}
                isSendingCode={isSendingCode}
                isOnCooldown={isOnCooldown}
                secondsLeft={secondsLeft}
                onNext={handleCodeNext}
                onResend={() => {
                  sendResetCode(wizardData.current.email!);
                }}
              />
            )}
            {currentStep === 3 && (
              <PasswordStep
                error={resetError}
                isLoading={isResetting || login.isPending}
                onSubmit={handlePasswordSubmit}
              />
            )}
          </View>
        </ScrollView>
      </ScreenContainer>
    </>
  );
}
